
import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, Loader2, Sparkles, Lock } from 'lucide-react';
import { WizardData } from './ProbateWizard';
import { IHTCalculation } from '../types';
import { ResearchMessage, performProbateResearch } from '../services/aiService';
import { useAuth } from './auth/AuthContext';
import ReactMarkdown from 'react-markdown';
import { PRICE_LABEL as PRICE } from './lib/commercialCopy';

const FREE_QUESTION_LIMIT = 2;

interface Props {
 data: WizardData;
 ihtCalc?: IHTCalculation | null;
 fullAccess?: boolean;
 estateId?: string | null;
 onUpgrade?: () => void;
 onShowAuth?: () => void;
}

/** Build a non-PII estate summary for the LLM system context */
function buildEstateContext(data: WizardData, ihtCalc?: IHTCalculation | null): string {
 const p = data.EstateProfile;
 const a = data.EstateAssets;
 const b = ihtCalc?.breakdown;

 const fmt = (n: number | null | undefined) =>
 n != null ? `£${n.toLocaleString('en-GB')}` : 'unknown';

 const lines: string[] = [];

 const dodYear = p.deceased?.date_of_death ? new Date(p.deceased.date_of_death).getFullYear() : null;
 if (dodYear) lines.push(`- Death: ${dodYear}`);
 if (p.deceased?.domicile) lines.push(`- Domicile: ${p.deceased.domicile}`);
 if (p.deceased?.maritalStatus) {
 const ms: Record<string, string> = {
 MARRIED_OR_CP: 'Married / Civil Partner',
 WIDOWED: 'Widowed',
 DIVORCED_OR_SEPARATED: 'Divorced / Separated',
 NEVER_MARRIED: 'Never married'
 };
 lines.push(`- Marital status: ${ms[p.deceased.maritalStatus] || p.deceased.maritalStatus}`);
 }

 const classes: string[] = [];
 if (a.properties?.length) classes.push(`${a.properties.length} propert${a.properties.length > 1 ? 'ies' : 'y'}`);
 if (a.bank_accounts?.length) classes.push(`${a.bank_accounts.length} bank account${a.bank_accounts.length > 1 ? 's' : ''}`);
 if (a.investments?.length) classes.push(`investments`);
 if (a.pensions?.length) classes.push(`pensions`);
 if (a.bpr_assets?.length) classes.push(`business/BPR assets`);
 if (a.apr_assets?.length) classes.push(`agricultural/APR assets`);
 if (classes.length) lines.push(`- Assets: ${classes.join(', ')}`);

 if (b?.gross_value != null) lines.push(`- Gross estate: ${fmt(b.gross_value)}`);
 if (b?.debts_total != null && b.debts_total > 0) lines.push(`- Debts: ${fmt(b.debts_total)}`);
 if (b?.net_value != null) lines.push(`- Net estate (after debts): ${fmt(b.net_value)}`);

 if (b?.spouse_exemption_total && b.spouse_exemption_total > 0)
 lines.push(`- Spouse/charity exemption: ${fmt(b.spouse_exemption_total)}`);
 if (b?.bpr_relief_total && b.bpr_relief_total > 0)
 lines.push(`- Business Property Relief (BPR): ${fmt(b.bpr_relief_total)}`);
 if (b?.apr_relief_total && b.apr_relief_total > 0)
 lines.push(`- Agricultural Property Relief (APR): ${fmt(b.apr_relief_total)}`);

 if (b?.nrb_available != null) lines.push(`- NRB available: ${fmt(b.nrb_available)}`);
 if (b?.rnrb_available != null && b.rnrb_available > 0) lines.push(`- RNRB available: ${fmt(b.rnrb_available)}`);

 if (b?.gifts_chargeable_total && b.gifts_chargeable_total > 0)
 lines.push(`- Chargeable lifetime gifts (7-year): ${fmt(b.gifts_chargeable_total)}`);

 if (b?.taxable_value != null) lines.push(`- Taxable estate: ${fmt(b.taxable_value)}`);
 if (ihtCalc?.total_tax_due != null) {
 const rate = b?.iht_rate_applied ?? b?.tax_rate;
 const rateLabel = rate ? ` at ${(rate * 100).toFixed(0)}%` : '';
 lines.push(`- IHT due: ${fmt(ihtCalc.total_tax_due)}${rateLabel}`);
 }

 return lines.join('\n');
}

function getSuggestedQuestions(data: WizardData, ihtCalc?: IHTCalculation | null): string[] {
 const a = data.EstateAssets;
 const b = ihtCalc?.breakdown;
 const questions: string[] = [];

 if (ihtCalc?.total_tax_due && ihtCalc.total_tax_due > 0)
 questions.push("When does the IHT need to be paid, and how?");
 if (b?.rnrb_available && b.rnrb_available > 0)
 questions.push("How does the Residence Nil Rate Band work in this situation?");
 if (a.bpr_assets?.length)
 questions.push("What are the qualifying conditions for Business Property Relief?");
 if (b?.gifts_chargeable_total && b.gifts_chargeable_total > 0)
 questions.push("How is taper relief applied to lifetime gifts?");
 questions.push("What IHT form does the executor need to file for this estate?");
 if (ihtCalc?.blockers?.length)
 questions.push("What information is still needed to finalise the IHT calculation?");

 return questions.slice(0, 4);
}

export const WizardResearchPanel: React.FC<Props> = ({
 data, ihtCalc, fullAccess = false, estateId, onUpgrade, onShowAuth,
}) => {
 const { user } = useAuth();
 const estateContext = buildEstateContext(data, ihtCalc);
 const suggestedQuestions = getSuggestedQuestions(data, ihtCalc);

 const [messages, setMessages] = useState<ResearchMessage[]>([]);
 const [input, setInput] = useState('');
 const [isLoading, setIsLoading] = useState(false);
 const [isOpen, setIsOpen] = useState(false);
 const [questionCount, setQuestionCount] = useState(0);
 const messagesEndRef = useRef<HTMLDivElement>(null);

 const limitReached = !fullAccess && questionCount >= FREE_QUESTION_LIMIT;

 useEffect(() => {
 messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
 }, [messages]);

 const handleSend = async (query?: string) => {
 const q = (query || input).trim();
 if (!q || isLoading) return;

 // Gate: anonymous
 if (!user) {
 onShowAuth?.();
 return;
 }

 // Gate: free limit
 if (limitReached) return;

 setInput('');
 setIsOpen(true);
 setMessages(prev => [...prev, { role: 'user', content: q }]);
 setIsLoading(true);

 try {
 const result = await performProbateResearch(
 q,
 messages,
 estateContext,
 fullAccess ? 'paid' : 'free',
 );
 setMessages(prev => [...prev, {
 role: 'assistant',
 content: result.text,
 sources: result.sources,
 }]);
 // Only count on successful answer
 if (!fullAccess) {
 setQuestionCount(c => c + 1);
 }
 } catch {
 setMessages(prev => [...prev, {
 role: 'assistant',
 content:"I couldn't reach the HMRC knowledge base. Please try again.",
 }]);
 } finally {
 setIsLoading(false);
 }
 };

 return (
 <div className="bg-white rounded border border-[#C7CBEA] overflow-hidden">
 {/* Header */}
 <div className="p-4 bg-[#3730A3] flex items-center justify-between">
 <div className="flex items-center gap-2">
 <div className="bg-white/20 p-1.5 rounded-lg">
 <Sparkles className="w-4 h-4 text-white" />
 </div>
 <div>
 <h3 className="font-bold text-white text-sm">Ask about this estate</h3>
 <p className="text-[#EEF0FA] text-[11px]">Personalised answers grounded in HMRC guidance</p>
 </div>
 </div>
 {!fullAccess && user && (
 <span className="text-[10px] bg-white/20 text-white px-2 py-1 rounded font-medium">
 {questionCount}/{FREE_QUESTION_LIMIT} free questions
 </span>
 )}
 </div>

 {/* Anonymous gate */}
 {!user && (
 <div className="p-5 text-center">
 <Lock className="w-5 h-5 text-[#AFA99F] mx-auto mb-2" />
 <p className="text-sm text-[#3F3D39] mb-1">Create a free account to ask questions about this estate</p>
 <p className="text-xs text-[#AFA99F] mb-3">Free account — no card required</p>
 <button
 onClick={() => onShowAuth?.()}
 className="text-xs font-medium text-white bg-[#3730A3] hover:bg-[#2E2780] px-4 py-2 rounded-lg transition-colors"
 >
 Create free account
 </button>
 </div>
 )}

 {/* Limit reached gate */}
 {user && limitReached && (
 <div className="p-5 text-center border-t border-[#E8E4DB]">
 <Lock className="w-5 h-5 text-[#AFA99F] mx-auto mb-2" />
 <p className="text-sm font-medium text-[#3F3D39] mb-1">You've used your free questions</p>
 <p className="text-xs text-[#6B6762] mb-3">
 Unlock the full report for unlimited guidance and more detailed answers.
 </p>
 <button
 onClick={() => onUpgrade?.()}
 className="text-xs font-medium text-white bg-[#3730A3] hover:bg-[#2E2780] px-4 py-2 rounded-lg transition-colors"
 >
 Unlock full report — {PRICE}
 </button>
 </div>
 )}

 {/* Suggested Questions — only when no messages yet and user can ask */}
 {user && !limitReached && messages.length === 0 && (
 <div className="p-4 space-y-2">
 <p className="text-xs font-bold text-[#6B6762] uppercase tracking-wider mb-3">Suggested questions</p>
 <div className="flex flex-wrap gap-2">
 {suggestedQuestions.map((q, i) => (
 <button
 key={i}
 onClick={() => handleSend(q)}
 disabled={isLoading}
 className="text-left text-xs px-3 py-2 bg-[#EEF0FA] hover:bg-[#EEF0FA] text-[#3730A3] rounded-lg border border-[#C7CBEA] transition-colors disabled:opacity-50"
 >
 {q}
 </button>
 ))}
 </div>
 </div>
 )}

 {/* Messages */}
 {(isOpen || messages.length > 0) && (
 <div className="max-h-96 overflow-y-auto p-4 space-y-4 border-t border-[#E8E4DB]">
 {messages.map((msg, idx) => (
 <div key={idx}>
 <div className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
 {msg.role === 'assistant' && (
 <div className="w-7 h-7 rounded-full bg-[#EEF0FA] flex items-center justify-center flex-shrink-0 mt-0.5">
 <Bot className="w-4 h-4 text-[#3730A3]" />
 </div>
 )}
 <div className="max-w-[85%]">
 <div className={`p-3 rounded text-sm leading-relaxed ${msg.role === 'user'
 ? 'bg-[#3730A3] text-white rounded-br-sm'
 : 'bg-[#FAFAF8] text-[#1A1A1A] border border-[#E8E4DB] rounded-bl-sm'}`}
 >
 {msg.role === 'user' ? msg.content : (
 <div className="prose prose-sm max-w-none prose-headings:text-[#1A1A1A] prose-a:text-[#3730A3]">
 <ReactMarkdown>{msg.content}</ReactMarkdown>
 </div>
 )}
 </div>
 {msg.sources && msg.sources.length > 0 && (
 <div className="mt-1.5 flex flex-wrap gap-1">
 {[...new Map(msg.sources.map(s => [s.uri, s])).values()].slice(0, 4).map((s, si) => (
 <a key={si} href={s.uri} target="_blank" rel="noopener noreferrer"
 className="text-[10px] text-[#6B6762] hover:text-[#3730A3] bg-[#FAFAF8] hover:bg-[#EEF0FA] px-2 py-0.5 rounded border border-[#E8E4DB] transition-colors truncate max-w-[200px]">
 {s.title}
 </a>
 ))}
 </div>
 )}
 </div>
 </div>

 {/* Inline upgrade prompt after each free assistant answer */}
 {msg.role === 'assistant' && !fullAccess && (
 <div className="ml-10 mt-2 flex items-center gap-2">
 <p className="text-xs text-[#AFA99F]">Want a more detailed answer based on your full report?</p>
 <button
 onClick={() => onUpgrade?.()}
 className="text-xs font-medium text-[#3730A3] hover:text-[#2E2780] hover:underline transition-colors whitespace-nowrap"
 >
 Unlock full report — {PRICE}
 </button>
 </div>
 )}
 </div>
 ))}
 {isLoading && (
 <div className="flex gap-3">
 <div className="w-7 h-7 rounded-full bg-[#EEF0FA] flex items-center justify-center flex-shrink-0">
 <Bot className="w-4 h-4 text-[#3730A3]" />
 </div>
 <div className="bg-[#FAFAF8] border border-[#E8E4DB] rounded rounded-bl-sm p-3">
 <Loader2 className="w-4 h-4 text-[#3730A3] animate-spin" />
 </div>
 </div>
 )}
 <div ref={messagesEndRef} />
 </div>
 )}

 {/* Input — shown when user is authed and hasn't hit limit */}
 {user && !limitReached && (
 <div className="p-3 border-t border-[#E8E4DB]">
 <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
 <input
 type="text"
 value={input}
 onChange={e => setInput(e.target.value)}
 placeholder="Ask a question about this estate..."
 disabled={isLoading}
 className="flex-1 text-sm border border-[#E8E4DB] rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#C7CBEA] focus:border-[#3730A3] focus:outline-none disabled:opacity-50"
 />
 <button
 type="submit"
 disabled={!input.trim() || isLoading}
 className="px-3 py-2 bg-[#3730A3] text-white rounded-lg hover:bg-[#2E2780] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
 >
 <Send className="w-4 h-4" />
 </button>
 </form>
 <p className="text-[10px] text-[#AFA99F] mt-1.5 text-center">
 Grounded in HMRC IHTM and GOV.UK guidance, personalised to your estate. Not legal or tax advice.
 </p>
 </div>
 )}
 </div>
 );
};
